import { BaseScreenProps } from '../types'
import ScreenTitle from '@/features/quiz/screen-title'
import React, { PropsWithChildren, ReactNode } from 'react'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { CreatePaymentLink } from '@/lib/apis/treatments'
import { isPaymentAlreadyReceivedError } from '@/lib/apis/patient/orders'
import { useStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { PricingValue, usePricingAmountCents } from '@/lib/cms/pricing'
import {
  RewardedPrice,
  RewardSaving,
  rewardId,
} from '@/features/rewards/web-loyalty-reward'
import EdFloatingContinueButton from './floating-continue-button'
import { EdPricingDetails } from './pricing-details'

type Props = Pick<BaseScreenProps, 'treatmentType' | 'rewardResponse'>

export default function ServiceSelectorScreen({
  treatmentType,
  rewardResponse,
}: Props) {
  const client = useApiClient()
  const router = useRouter()
  const paymentLinkMutation = useMutation(
    CreatePaymentLink.mutation(client, treatmentType),
  )

  const showNotification = useStore((state) => state.showNotification)
  const isEd = treatmentType === 'ed'
  const baseAmountCents = usePricingAmountCents('treatmentInitialConsultation')

  function continueToPayment() {
    paymentLinkMutation.mutate(
      { rewardId: rewardId(rewardResponse) },
      {
        onSuccess: (data) => {
          window.location.assign(data.checkoutUrl)
        },
        onError: (error) => {
          // The earlier checkout was paid, so retrying here would only fail again.
          // The portal shows the request's progress.
          if (isPaymentAlreadyReceivedError(error)) {
            showNotification(
              new NotificationItem(
                'Payment already received.',
                NotificationItemVariant.Success,
              ),
            )
            router.push('/portal')
            return
          }

          showNotification(
            new NotificationItem(
              'Something went wrong. Please try again later',
              NotificationItemVariant.Error,
            ),
          )
        },
      },
    )
  }

  return (
    <>
      {isEd ? (
        <EdPricingDetails
          baseAmountCents={baseAmountCents}
          rewardResponse={rewardResponse}
        />
      ) : (
        <div className="mb-16 w-full">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-4 sm:space-y-5 lg:space-y-6">
              <ScreenTitle title="Review your consultation pricing" />
              <div className="pt-4">
                <PricingInfoCard rewardResponse={rewardResponse} />
              </div>
              <p className="text-sm text-blue-800">
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  className="mr-1.5"
                />
                If we find that our treatments aren{"'"}t a good fit for you, we
                {"'"}ll refund your initial consultation fee.
              </p>
            </div>
          </div>
        </div>
      )}

      {isEd ? (
        <EdFloatingContinueButton
          onClick={continueToPayment}
          disabled={paymentLinkMutation.isPending}
          loading={paymentLinkMutation.isPending}
        >
          Continue to payment
        </EdFloatingContinueButton>
      ) : (
        <BottomContinueButton
          onClick={continueToPayment}
          initial={true}
          show={true}
          buttonProps={{
            children: 'Continue to payment',
            loading: paymentLinkMutation.isPending,
          }}
        />
      )}
    </>
  )
}

function PricingInfoCard({ rewardResponse }: Pick<Props, 'rewardResponse'>) {
  return (
    <div className="space-y-3 border-y border-gray-200 bg-white py-3 sm:space-y-6 sm:rounded-lg sm:border sm:p-6">
      <PricingSection title="To pay now">
        <PricingRow
          label="Initial consultation"
          value={
            <RewardedPrice
              response={rewardResponse}
              fallback={<PricingValue name="treatmentInitialConsultation" />}
              layout="inline"
            />
          }
        />
        <RewardSaving response={rewardResponse} className="mt-2" />
      </PricingSection>

      <div className="border-t border-gray-200" />

      <PricingSection title="After your plan is approved">
        <p className="mb-3 text-sm text-gray-600">
          You{"'"}ll be able to choose between:
        </p>

        <div className="space-y-4">
          <PricingOption title="Script-only plan">
            <p className="text-sm text-gray-600">
              E-scripts are included with your initial consultation. Afterwards:
            </p>
            <ul className="mt-1 list-inside list-disc text-sm text-gray-600">
              <li>$39.90 every 2 months, or</li>
              <li>$179.50 per year</li>
            </ul>
          </PricingOption>

          <PricingOption title="With medication delivery">
            <p className="text-sm text-gray-600">
              Price depends on your approved treatment plan. Includes free
              discreet delivery every 2 months.
            </p>
          </PricingOption>
        </div>
      </PricingSection>
    </div>
  )
}

function PricingSection({
  title,
  children,
}: PropsWithChildren<{ title: string }>) {
  return (
    <div>
      <h3 className="mb-3 font-sans text-base font-semibold text-rich-black">
        {title}
      </h3>
      {children}
    </div>
  )
}

function PricingRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="font-sans text-sm font-medium text-rich-black">
        {value}
      </span>
    </div>
  )
}

function PricingOption({
  title,
  children,
}: PropsWithChildren<{ title: string }>) {
  return (
    <div className="rounded-md bg-gray-50 p-4">
      <h4 className="mb-2 font-sans text-sm font-semibold text-rich-black">
        {title}
      </h4>
      {children}
    </div>
  )
}
